import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import { AtStateStore } from '@bindoc-projects/geographic-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const AT_STATE_IDS_CONDITION_ID = 'stateIdAT';
export const AtFilterConditionStateIdPluginType: FilterConditionPluginSpecification<
  number[],
  AtFilterConditionStateIdDto,
  AtStateIdsCondition
> = {
  type: 'stateIdsAT',
  category: 'case',
  displayName: { de: 'Bundesland' },
  sortingIndex: 1,
  occurrence: 'single',
  fromObject(
    obj: AtFilterConditionStateIdDto,
  ): FilterConditionPluginImpl<number[]> {
    return new AtFilterConditionStateIdPlugin(obj.stateIds);
  },
  fromCondition(
    jsonObj: AtStateIdsCondition,
  ): FilterConditionPluginImpl<number[]> {
    return new AtFilterConditionStateIdPlugin(jsonObj.includes ?? []);
  },
  newInstance(): FilterConditionPluginImpl<number[]> {
    return new AtFilterConditionStateIdPlugin([]);
  },
};

export class AtFilterConditionStateIdPlugin extends FilterConditionPluginImpl<
  number[],
  AtFilterConditionStateIdDto
> {
  static override Specification: FilterConditionPluginSpecification<
    number[],
    AtFilterConditionStateIdDto,
    AtStateIdsCondition
  > = AtFilterConditionStateIdPluginType;

  constructor(private stateIds: number[]) {
    super(AtFilterConditionStateIdPluginType.type);
  }

  clone(): AtFilterConditionStateIdPlugin {
    return new AtFilterConditionStateIdPlugin([...(this.stateIds ?? [])]);
  }

  override equals(other: AtFilterConditionStateIdPlugin): boolean {
    return (
      super.equals(other) && BdArrayUtil.equals(this.stateIds, other.stateIds)
    );
  }

  override validationToText(): string {
    if (this.stateIds.length === 0) {
      return 'Mindestens ein Bundesland auswählen';
    } else {
      return '';
    }
  }

  toCondition(): BdCondition | null {
    return new AtStateIdsCondition(this.stateIds, []);
  }

  toJSON(): AtFilterConditionStateIdDto | null {
    return {
      type: this.type,
      stateIds: this.stateIds,
    };
  }

  toText(): string | null {
    return this.stateIds.length === 1
      ? 'ein Bundesland'
      : `${this.stateIds.length} Bundesländer`;
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.stateIds.map((id) => {
      const state = AtStateStore.byId(id);
      return state ? state.name : String(id);
    });
    return { title, items };
  }

  toValue(): number[] {
    return this.stateIds;
  }
}

export class AtStateIdsCondition extends BdContentCondition<number> {
  constructor(includes: number[], excludes: number[]) {
    super(AT_STATE_IDS_CONDITION_ID, includes, excludes);
  }
}

interface AtFilterConditionStateIdDto {
  type: string;
  stateIds: number[];
}
